import { findAnswerTargetElement, getAnswerTargetScrollTop } from 'lib/answerTargetScroll'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

// Visibility is inherited and a descendant can override it. Opacity gates the entire
// painted subtree, including Panel's explicitly visible content and media surfaces.
export const OPEN_PLACEMENT_HIDDEN_STYLE = {
    transition: 'none',
    visibility: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
} as const

// The one-shot open-placement intent for a quest+answer filter. Returns undefined unless
// instant_reply_nav is on, so flag-off callers spread nothing and stay byte-identical.
export function openPlacementIntentForFilter(filter?: {
    questId?: string | number
    answerId?: string | number
}) {
    return filter?.questId && filter?.answerId && userHasFeature('instant_reply_nav')
        ? { questId: String(filter.questId), answerId: String(filter.answerId) }
        : undefined
}

// A pre-paint open placement only ever moves a container that is still at rest at the
// top. Anything beyond this offset means the user (or a competing reveal) already owns
// the scroll position, and placement must fall back to the animated reveal pipeline.
export const OPEN_PLACEMENT_MAX_START_SCROLL_TOP_PX = 1
export const OPEN_PLACEMENT_TOLERANCE_PX = 1

export type OpenPlacementResult = {
    placed: boolean
    targetScrollTop: number
    appliedScrollTop: number
}

export function answerRowSelector(answerId: string) {
    return `[data-answer-row-id="${String(answerId).replace(/"/g, '\\"')}"]`
}

// Resolves the placeable element for an answer: the hydrated post surface when one is
// mounted, else the row element that reserves its geometry. Natural-thread hydrating
// rows carry data-answer-row-id before any answer surface exists, so a skeleton target
// is placeable (and anchorable) in the same commit that first renders it.
export function findAnswerRowElement(
    root: ParentNode | null | undefined,
    answerId: string,
): HTMLElement | null {
    if (!root || !answerId) return null

    return (
        findAnswerTargetElement(root, answerId) ||
        (root.querySelector?.(answerRowSelector(answerId)) as HTMLElement | null) ||
        null
    )
}

// The one scroll write of an open placement. Reads the row's real laid-out position —
// never a predicted offset — and places it below the panel top with the same math the
// reveal pipeline verifies against. Runs inside a layout effect, so the write lands
// before first paint.
export function placeAnswerRowAtTarget(
    scrollElement: HTMLElement,
    rowElement: HTMLElement,
): OpenPlacementResult {
    const targetScrollTop = getAnswerTargetScrollTop(scrollElement, rowElement)
    scrollElement.scrollTop = targetScrollTop
    const appliedScrollTop = scrollElement.scrollTop || 0

    return {
        placed: Math.abs(appliedScrollTop - targetScrollTop) <= OPEN_PLACEMENT_TOLERANCE_PX,
        targetScrollTop,
        appliedScrollTop,
    }
}
